import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { QrCode, Clock, CheckCircle2, XCircle, RefreshCw, Copy, Check, ArrowLeft } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import MerchantLayout from "@/shared/components/merchant/MerchantLayout";
import StatusBadge from "@/shared/components/merchant/StatusBadge";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

export default function MerchantDeposit() {
  const { t } = useLanguage();
  const [step, setStep] = useState("form"); // form, qr, success, expired
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(900); // 15 minutes in seconds
  const [copied, setCopied] = useState(false);

  // Timer countdown
  useEffect(() => {
    if (step === "qr" && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setStep("expired");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [step, timeLeft]);

  // Auto-refresh status simulation
  useEffect(() => {
    if (step === "qr") {
      const checkInterval = setInterval(() => {
        // Simulate random success after some time
        if (Math.random() > 0.95) {
          setStep("success");
        }
      }, 3000);
      return () => clearInterval(checkInterval);
    }
  }, [step]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleGenerate = async () => {
    if (!amount || parseFloat(amount) < 20) return;
    setLoading(true);
    // Simulate API call
    await new Promise((r) => setTimeout(r, 1500));
    setLoading(false);
    setTimeLeft(900);
    setStep("qr");
  };

  const handleReset = () => {
    setStep("form");
    setAmount("");
    setReference("");
    setTimeLeft(900);
  };

  const copyAmount = () => {
    navigator.clipboard.writeText(amount);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <MerchantLayout currentPage="MerchantDeposit" merchantName="Royal Gaming" merchantCode="MRC001">
      <div className="max-w-lg mx-auto">
        <AnimatePresence mode="wait">
          {step === "form" && (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{t("deposits")} {t("qrCode")}</h1>
                <p className="text-slate-500">{t("promptPayConfig")}</p>
              </div>

              <div className="bg-white rounded-2xl p-6 border border-slate-100">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="amount">{t("amountThb")} *</Label>
                    <Input
                      id="amount"
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                      min="20"
                      max="2000000"
                      className="text-lg"
                    />
                    <p className="text-xs text-slate-500">{t("minimumAmount")}: ฿20 • {t("maximumAmount")}: ฿2,000,000</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reference">{t("reference")} ({t("optionalCustomerRef")})</Label>
                    <Input
                      id="reference"
                      value={reference}
                      onChange={(e) => setReference(e.target.value)}
                      placeholder={t("orderIdPlaceholder")}
                    />
                  </div>

                  <Button
                    onClick={handleGenerate}
                    disabled={loading || !amount || parseFloat(amount) < 20}
                    className="w-full bg-blue-600 hover:bg-blue-700 gap-2"
                  >
                    {loading ? (
                      <>
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        {t("creating")}
                      </>
                    ) : (
                      <>
                        <QrCode className="h-4 w-4" />
                        {t("create")} {t("qrCode")}
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <h4 className="font-medium text-blue-900 mb-2">{t("howRoutingWorks")}</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>1. {t("enterDepositAmount")}</li>
                  <li>2. {t("create")} {t("qrCode")}</li>
                  <li>3. {t("customerScansQr")}</li>
                  <li>4. {t("paymentProcessed")}</li>
                </ul>
              </div>
            </motion.div>
          )}

          {step === "qr" && (
            <motion.div
              key="qr"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              <Button variant="ghost" onClick={handleReset} className="gap-2 -ml-2">
                <ArrowLeft className="h-4 w-4" />
                {t("back")}
              </Button>

              <div className="bg-white rounded-2xl p-6 border border-slate-100 text-center">
                {/* Timer */}
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 ${timeLeft < 60 ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"
                  }`}>
                  <Clock className="h-4 w-4" />
                  <span className="font-mono font-medium">{formatTime(timeLeft)}</span>
                </div>

                {/* QR Code */}
                <div className="relative inline-block mb-6">
                  <div className="w-64 h-64 bg-white border-2 border-slate-200 rounded-2xl p-4 mx-auto">
                    {/* Simulated QR Code Pattern */}
                    <div className="w-full h-full bg-gradient-to-br from-slate-900 to-slate-700 rounded-lg flex items-center justify-center">
                      <QrCode className="h-32 w-32 text-white" />
                    </div>
                  </div>
                  <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-medium px-3 py-1 rounded-full">
                    PromptPay
                  </div>
                </div>

                {/* Amount */}
                <div className="mb-4">
                  <p className="text-sm text-slate-500 mb-1">{t("amount")}</p>
                  <div className="flex items-center justify-center gap-2">
                    <p className="text-3xl font-bold text-slate-900">฿{parseFloat(amount).toLocaleString()}</p>
                    <button
                      onClick={copyAmount}
                      className="text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {copied ? <Check className="h-5 w-5 text-emerald-500" /> : <Copy className="h-5 w-5" />}
                    </button>
                  </div>
                  {reference && (
                    <p className="text-sm text-slate-500 mt-1">{t("ref")}: {reference}</p>
                  )}
                </div>

                {/* Status */}
                <div className="flex items-center justify-center gap-2 text-slate-600">
                  <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                  {t("pending")}...
                </div>
              </div>

              <div className="bg-slate-50 rounded-xl p-4 text-center text-sm text-slate-600">
                <p>{t("status")} {t("refresh")}</p>
              </div>
            </motion.div>
          )}

          {step === "success" && (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-6"
            >
              <div className="bg-white rounded-2xl p-8 border border-slate-100 text-center">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="h-10 w-10 text-emerald-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">{t("completed")}!</h2>
                <p className="text-slate-500 mb-6">
                  {t("deposit")} ฿{parseFloat(amount).toLocaleString()} {t("completed").toLowerCase()}.
                </p>
                <div className="bg-emerald-50 rounded-xl p-4 mb-6">
                  <StatusBadge status="success" />
                  <p className="text-sm text-slate-600 mt-2">
                    {t("transactionId")}: TXN{Date.now().toString().slice(-10)}
                  </p>
                </div>
                <Button onClick={handleReset} className="gap-2">
                  <QrCode className="h-4 w-4" />
                  {t("create")} {t("qrCode")}
                </Button>
              </div>
            </motion.div>
          )}

          {step === "expired" && (
            <motion.div
              key="expired"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-6"
            >
              <div className="bg-white rounded-2xl p-8 border border-slate-100 text-center">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="h-10 w-10 text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">{t("qrCode")} {t("expired")}</h2>
                <p className="text-slate-500 mb-6">
                  {t("expired")} {t("qrCode")}.
                </p>
                <Button onClick={handleReset} className="gap-2 bg-blue-600 hover:bg-blue-700">
                  <RefreshCw className="h-4 w-4" />
                  {t("retry")}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </MerchantLayout>
  );
}